import { Box, BoxProps, styled } from "@mui/material";
import { FILE_DOMAIN } from "constants/schemas";
import { useEffect, useState, useRef, Fragment, memo, useCallback } from "react";

const HtmlViewer = ({
  content = "",
  isHtmlNormal = false,
  ...otherProps
}: { content: string, isHtmlNormal?: boolean } & BoxProps) => {
  const [updatedHtml, setUpdateHtml] = useState("");
  const [iframeHeight, setIframeHeight] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const resizeTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;

    // Update all <h1> tags to <h2>
    const h1Elements = tempDiv.querySelectorAll("h1");
    h1Elements.forEach((h1) => {
      const h2 = document.createElement("h2");
      h2.innerHTML = h1.innerHTML;
      h1.replaceWith(h2);
    });

    // Update <img> tags with non-absolute URLs
    const imgElements = tempDiv.querySelectorAll("img");
    imgElements.forEach((img) => {
      const currentSrc = img.getAttribute("src");
      if (
        currentSrc &&
        !currentSrc.startsWith("http") &&
        !currentSrc.startsWith("data:image/")
      ) {
        img.setAttribute("src", `${FILE_DOMAIN}${currentSrc}`);
      }
      img.setAttribute("loading", "lazy");
      img.setAttribute("style", "max-width: 100%; height: auto;");
    });

    setUpdateHtml(tempDiv.innerHTML);
  }, [content]);

  // Improved resize function with proper height calculation
  const resizeIframe = useCallback(() => {
    if (!iframeRef.current?.contentWindow?.document) return;

    try {
      const iframeDoc = iframeRef.current.contentWindow.document;
      const body = iframeDoc.body;
      
      // Use only scrollHeight which is most reliable
      const contentHeight = body.scrollHeight;
      const newHeight = contentHeight + 10; // Minimal padding

      // Only update if there's a significant difference (>5px) to avoid micro-adjustments
      if (Math.abs(newHeight - iframeHeight) > 5 && newHeight > 0 && newHeight < 10000) {
        setIframeHeight(newHeight);
      }
    } catch (error) {
      console.warn('Error calculating iframe height:', error);
    }
  }, [iframeHeight]);

  // Handle messages from iframe with improved validation
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'resize' && typeof event.data.height === 'number') {
        const newHeight = event.data.height + 10;
        // More lenient validation for window resize scenarios
        if (newHeight > 0 && newHeight < 15000 && Math.abs(newHeight - iframeHeight) > 8) {
          setIframeHeight(newHeight);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [iframeHeight]);

  // Setup iframe event listeners with improved throttling
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    let isResizing = false;
    const throttledResize = () => {
      if (isResizing) return;
      isResizing = true;
      setTimeout(() => {
        resizeIframe();
        isResizing = false;
      }, 150);
    };

    const handleLoad = () => {
      // Initial resize after load
      setTimeout(() => {
        resizeIframe();
      }, 200);
      
      // Setup image load listeners
      try {
        const iframeDoc = iframe.contentWindow?.document;
        if (iframeDoc) {
          const images = iframeDoc.querySelectorAll('img');
          images.forEach(img => {
            if (!img.complete) {
              img.addEventListener('load', throttledResize, { once: true });
              img.addEventListener('error', throttledResize, { once: true });
            }
          });
        }
      } catch (error) {
        console.warn('Error setting up iframe listeners:', error);
      }
    };

    iframe.addEventListener('load', handleLoad, { once: true });
    
    const timeout = resizeTimeoutRef.current;
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [updatedHtml, resizeIframe]);

  const updatedHtmlWithErrorHandler = `
    <!DOCTYPE html>
    <html style="height: auto;">
    <head>
      <meta charset="UTF-8">
      <style>
        html, body {
          margin: 0;
          padding: 0;
          overflow: hidden;
          height: auto;
          min-height: auto;
          font-family: "Inter", sans-serif;
          line-height: 1.43;
          font-size: 14px;
          overflow-wrap: anywhere;
        }
        * {
          box-sizing: border-box;
          max-width: 100%;
          float: initial;
        }
        p {
          margin-block-start: 0;
          margin-block-end: 0;
        }
        p:has(img) {
          text-align: center;
        }
        p a {
          text-align: center;
        }
        img {
          max-width: 100%;
          height: auto;
          margin: 0 auto;
        }
        table, th, td {
          border: 1px solid black;
          border-collapse: collapse;
        }
        table {
          margin: 0 auto;
        }
        .ql-code-block {
          white-space: wrap;
        }
        @media (max-width: 768px) {
          body {
            font-size: 14px;
          }
        }
      </style>
    </head>
    <body>
      <script>
        (function() {
          let lastNotifiedHeight = 0;
          let resizeTimeout;
          
          const notifyResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
              const contentHeight = Math.max(
                document.body.scrollHeight,
                document.body.offsetHeight
              );
              
              // Only notify if height changed significantly (>10px difference)
              if (window.parent && window.parent !== window && 
                  contentHeight > 0 && 
                  Math.abs(contentHeight - lastNotifiedHeight) > 10) {
                lastNotifiedHeight = contentHeight;
                window.parent.postMessage({ 
                  type: 'resize', 
                  height: contentHeight 
                }, '*');
              }
            }, 150);
          };

          // Handle image errors
          const handleImages = () => {
            document.querySelectorAll("img").forEach(img => {
              img.onerror = function() {
                this.style.display = "none";
                // Recalculate height when image fails to load
                setTimeout(notifyResize, 100);
              };
            });
          };

          const initialize = () => {
            handleImages();
            
            // Initial resize notification
            setTimeout(() => {
              if (document.fonts) {
                document.fonts.ready.then(notifyResize);
              } else {
                notifyResize();
              }
            }, 100);

            // Handle window resize with throttling
            let windowResizeTimeout;
            window.addEventListener('resize', () => {
              clearTimeout(windowResizeTimeout);
              windowResizeTimeout = setTimeout(notifyResize, 300);
            });
          };

          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', initialize);
          } else {
            initialize();
          }
        })();
      </script>
      ${updatedHtml}
    </body>
    </html>
  `;

  return (
    <Fragment>
      {isHtmlNormal && (
        <StyledBox 
          {...otherProps} 
          component="div" 
          dangerouslySetInnerHTML={{ __html: updatedHtmlWithErrorHandler }} 
        />
      )}
      {!isHtmlNormal && (
        <StyledBox {...otherProps} component="div">
          <iframe
            ref={iframeRef}
            title="html-viewer-iframe"
            srcDoc={updatedHtmlWithErrorHandler}
            style={{
              display: "block",
              width: "100%",
              border: "none",
              overflow: "hidden",
              height: iframeHeight > 0 ? `${iframeHeight}px` : 'auto',
              minHeight: iframeHeight > 0 ? `${iframeHeight}px` : '100px',
            }}
          />
        </StyledBox>
      )}
    </Fragment>
  );
};

// Simplified StyledBox - chỉ cần thiết cho container
const StyledBox = styled(Box)({
  overflow: "hidden !important",
  // Chỉ style cho iframe, không ảnh hưởng các phần tử khác
  "& iframe": {
    overflow: "hidden !important",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
});

export default memo(HtmlViewer);